import { useMemo, useState } from 'react';
import {
  Badge,
  Body1,
  Body1Strong,
  Caption1,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';

import { NEVER_COPIED_FROM_PREVIOUS, TPreviousChange } from '../../models';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    maxHeight: '260px',
    overflowY: 'auto',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selected: { border: `2px solid ${tokens.colorBrandStroke1}`, backgroundColor: tokens.colorBrandBackground2 },
  muted: { color: tokens.colorNeutralForeground3 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  summary: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

type TProps = {
  previousChanges: TPreviousChange[];
  selectedId?: string;
  onSelect: (id: string) => void;
};

export const PreviousChangePicker = ({ previousChanges, selectedId, onSelect }: TProps) => {
  const classes = useStyles();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase();
    if (!normalized) return previousChanges;
    return previousChanges.filter((change) =>
      `${change.displayId} ${change.title}`.toLocaleLowerCase().includes(normalized)
    );
  }, [previousChanges, search]);

  const selected = previousChanges.find((change) => change.id === selectedId);

  return (
    <div className={classes.root}>
      <Input
        contentBefore={<SearchRegular />}
        placeholder="Search by ID or title"
        value={search}
        onChange={(_, data) => setSearch(data.value)}
      />
      <div className={classes.list}>
        {filtered.map((change) => (
          <button
            key={change.id}
            type="button"
            className={mergeClasses(classes.card, selectedId === change.id && classes.selected)}
            onClick={() => onSelect(change.id)}
            aria-pressed={selectedId === change.id}
          >
            <Caption1 className={classes.muted}>{change.displayId}</Caption1>
            <Body1Strong>{change.title}</Body1Strong>
            <Caption1 className={classes.muted}>{change.summary}</Caption1>
          </button>
        ))}
        {filtered.length === 0 ? <Caption1 className={classes.muted}>No changes match your search.</Caption1> : null}
      </div>

      {selected ? (
        <div className={classes.summary}>
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Only structure is copied</MessageBarTitle>
              These are never copied: {NEVER_COPIED_FROM_PREVIOUS.join(', ')}.
            </MessageBarBody>
          </MessageBar>
          <div className="flex flex-col gap-1">
            <Body1Strong>Reusable structure</Body1Strong>
            <div className={classes.chips}>
              {selected.reusableStructure.map((item) => (
                <Badge key={item} appearance="outline" color="informative">
                  {item}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Body1Strong>Suggested tasks</Body1Strong>
            {selected.suggestedTasks.map((task) => (
              <Body1 key={task.title}>
                {task.title} <span className={classes.muted}>· {task.assignedFunction}</span>
              </Body1>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
